import { isEmpty, pick, compact } from 'lodash';
import { getBookableOfferedSolutions } from '../../components/booking/OfferedSolution.helpers';
import { buildWaypoints, initialPaymentMethods, rideIsBookable } from './predicates';

const apiErrorMessages = error => error.responseJSON.api_errors.map(item => item.message);

// Holds the API driven part of the booking wizard. It reads and writes the component state through the accessors it
// is given, so it can be exercised without rendering the component.
export default class BookingWizardController {
  constructor({ api, props, getState, setState, setOption, startTicker, endTicker }) {
    this.api = api;
    this.props = props;
    this.getState = getState;
    this.setState = setState;
    this.setOption = setOption;
    this.startTicker = startTicker;
    this.endTicker = endTicker;

    this.createRideInquiry = this.createRideInquiry.bind(this);
    this.createRide = this.createRide.bind(this);
    this.bookRide = this.bookRide.bind(this);
    this.cancelRide = this.cancelRide.bind(this);
    this.pollRide = this.pollRide.bind(this);
    this.fetchUserBootstrap = this.fetchUserBootstrap.bind(this);
  }

  // STATE HELPERS -------------------------------------------------------------

  showSpinner(spinnerMessage) {
    this.setState({ spinnerMessage });
  }

  hideSpinner() {
    this.setState({ spinnerMessage: null });
  }

  setErrors(errors) {
    this.setState({ errors });
  }

  handleApiError(error) {
    if (error.status === 422) {
      this.setState({ spinnerMessage: null, errors: apiErrorMessages(error) });
    }
  }

  lookupAreas(ids, areaType) {
    return this.props.areas.filter(area => area.area_type === areaType && ids.includes(area.id));
  }

  setServedAreas(servedAreaIds) {
    this.setState({ servedAreas: this.lookupAreas(servedAreaIds, 'served_area') });
  }

  setUnservedAreas(unservedAreaIds) {
    this.setState({ unservedAreas: this.lookupAreas(unservedAreaIds, 'unserved_area') });
  }

  setAssistances(assistances) {
    const assistanceErrors = [];
    const assistanceWarnings = [];
    assistances.forEach(a => (a.error_code ? assistanceErrors : assistanceWarnings).push(a.text));

    // update state with the assistances objects, additionally with errors/warnings if existent
    const stateUpdate = { assistances };
    if (assistanceErrors.length > 0) {
      stateUpdate.errors = (this.getState().errors || []).concat(assistanceErrors);
    }
    if (assistanceWarnings.length > 0) {
      stateUpdate.warnings = (this.getState().warnings || []).concat(assistanceWarnings);
    }

    this.setState(stateUpdate);
  }

  // HEAVY API HITTING OPERATIONS ----------------------------------------------

  createRideInquiry() {
    const state = this.getState();
    const rideInquiryData = {
      user_id: state.userId,
      passengers: compact(state.passengers),
      ...buildWaypoints({ state, product: this.props.product }),
      options: state.options
    };

    this.setState({
      errors: null,
      warnings: null
    });
    this.showSpinner(window.locales.BookingWizard.spinners.inquiring);

    this.api
      .createRideInquiry(rideInquiryData)
      .done(rideInquiryResult => {
        this.setServedAreas(rideInquiryResult.data.served_area_ids);
        this.setUnservedAreas(rideInquiryResult.data.unserved_area_ids);
        this.setAssistances(rideInquiryResult.data.assistances);

        this.hideSpinner();
      })
      .fail(error => this.handleApiError(error));
  }

  createRide() {
    const state = this.getState();
    const rideData = {
      user_id: state.userId,
      passengers: compact(state.passengers),
      ...buildWaypoints({ state, product: this.props.product }),
      options: state.options,
      passenger_note_to_driver: state.passengerNoteToDriver
    };

    this.setErrors(null);
    this.showSpinner(window.locales.BookingWizard.spinners.searching);

    this.api
      .createRide(rideData)
      .done(rideResult => {
        this.setState({ ride: rideResult.data });
        window.setTimeout(this.pollRide, 2000);
      })
      .fail(error => this.handleApiError(error));
  }

  bookRide() {
    const { ride, chosenPaymentMethod } = this.getState();
    if (!ride || ride.state !== 'ready') return;

    this.setErrors(null);
    this.showSpinner(window.locales.BookingWizard.spinners.waiting_for_driver);

    const bookingData = {};

    if (chosenPaymentMethod) {
      bookingData.payment_method = pick(chosenPaymentMethod, 'payment_method_type', 'id');
    }

    const offeredSolutions = getBookableOfferedSolutions(ride);
    if (offeredSolutions.length > 0) {
      const bookedSolution = offeredSolutions[0];
      bookingData.solution_id = bookedSolution.id;
    }

    this.api
      .bookRide(ride.id, bookingData)
      .done(() => {
        this.pollRide();
      })
      .fail(error => this.handleApiError(error));
  }

  cancelRide() {
    const { ride } = this.getState();
    if (!ride) return;

    this.setErrors(null);

    if (ride.state === 'cancelled') {
      // The ride was already cancelled by the system (no vehicle available, etc)
      this.setState({ ride: null });
      return;
    }

    // The ride is in any other state
    let cancellationConfirmed = true;

    if (ride.state === 'passenger_accepted' || ride.state === 'driver_accepted') {
      // eslint-disable-next-line no-alert
      cancellationConfirmed = window.confirm(window.locales.BookingWizard.message.cancelConfirmation);
    }

    if (cancellationConfirmed) {
      this.api.cancelRide(ride.id).done(() => {
        this.setState({
          spinnerMessage: null,
          ride: null
        });
      });
    }
  }

  pollRide() {
    const { ride, spinnerMessage } = this.getState();
    if (!ride) return;

    if (!spinnerMessage) {
      this.showSpinner(window.locales.BookingWizard.spinners.refreshing);
    }

    this.api.getRide(ride.id).done(rideResult => {
      this.setState({ ride: rideResult.data });

      // When the ride was cancelled
      if (rideResult.data.state === 'cancelled') {
        const cancellationReasons = [rideResult.data.cancellation_reason_translated];

        this.setErrors(cancellationReasons);
      }

      // When the ride was accepted by the driver
      if (rideResult.data.state === 'driver_accepted') {
        this.setState({
          spinnerMessage: null,
          errors: null
        });

        if (!rideResult.data.pickup) {
          window.setTimeout(this.pollRide, 2000);
        }
      }

      if (rideResult.data.state === 'searching' || rideResult.data.state === 'passenger_accepted') {
        // Searching for a ride, or waiting for driver, reload every 2s,
        // Stop the ticker, since the customer does not need to do anything anymore
        window.setTimeout(this.pollRide, 2000);
        this.endTicker();
      } else if (rideResult.data.state === 'ready') {
        if (rideIsBookable({ ride: rideResult.data })) {
          // Offered Ride to the passenger, reload every 10s to wait for timeout,
          // start the ticker to show time left countdown
          this.startTicker();
          window.setTimeout(this.pollRide, 10000);
        } else {
          this.setErrors([window.locales.BookingWizard.errors.no_bookable_solutions]);
          this.endTicker();
        }
        this.hideSpinner();
      } else {
        this.hideSpinner();
      }
    });
  }

  fetchUserBootstrap(userId) {
    this.api
      .fetchUserBootstrap(userId)
      .done(bootstrapData => {
        const {
          payment_methods: bootstrapPaymentMethods,
          requested_points: previouslyRequestedPoints,
          last_ride_options: lastRideOptions,
          outstanding_payments: outstandingPayments,
          first_name: firstName,
          last_name: lastName,
          warnings
        } = bootstrapData.data;
        const { cloningRide, product } = this.props;
        const { passengers } = this.getState();

        const paymentMethods = bootstrapPaymentMethods.map(paymentMethod =>
          pick(paymentMethod, 'payment_method_type', 'details', 'id')
        );

        // Now append the payment methods that have already been there, set the state to the new paymentMethods and
        // select the first payment method of those in the state or skip that if cloning a ride
        paymentMethods.push(...initialPaymentMethods(this.props.product));

        const newState = { paymentMethods, previouslyRequestedPoints, warnings: [] };

        if (isEmpty(cloningRide.payment_method)) {
          const chosenPaymentMethod = paymentMethods[0];
          newState.chosenPaymentMethod = chosenPaymentMethod;
        }

        // when we are cloning a ride, we just copy over all the data from the cloned
        // ride and don't need to do anything here
        if (isEmpty(cloningRide)) {
          // when we are not cloning a ride and it is activated that the previous ride
          // settings should be copied over, copy over the ride settings
          if (!!product.booking_wizard_settings.use_last_ride_options && !isEmpty(lastRideOptions)) {
            newState.passengers = lastRideOptions.passengers;

            Object.entries(lastRideOptions.options).forEach(option => {
              this.setOption(option[0], option[1]);
            });
          } else {
            newState.passengers = passengers;
          }

          if (!isEmpty(warnings)) {
            newState.warnings = warnings.map(item => item);
          }

          // now let's make sure we have a name set on the first passenger
          if (!isEmpty(newState.passengers)) {
            newState.passengers[0].first_name = newState.passengers[0].first_name || firstName;
            newState.passengers[0].last_name = newState.passengers[0].last_name || lastName;
          }
        }

        if (outstandingPayments && outstandingPayments.has_outstanding_payments === true) {
          const number = outstandingPayments.outstanding_total_amount / 100.0;
          let langFormat = document.querySelector('html').getAttribute('lang');
          if (!langFormat) {
            langFormat = 'en-BZ';
          }

          const formattedAmount = new Intl.NumberFormat(langFormat, {
            style: 'currency',
            currency: outstandingPayments.currency
          }).format(number);

          const newWarning = window.locales.BookingWizard.warnings.outstanding_payments
            .replace('%{rides}', outstandingPayments.num_of_outstanding_payments)
            .replace('%{amount}', formattedAmount);

          newState.warnings.push(newWarning);
        }

        this.setState(newState);
      })
      .fail(error => this.handleApiError(error));
  }
}
